import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Camera, AlertCircle, CheckCircle2, Loader2, PencilLine } from 'lucide-react';
import { aiPurchasesAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

function validateFile(file) {
  const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase();
  if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExt)) {
    return 'Only PDF and image files are allowed';
  }
  if (file.size > MAX_SIZE_BYTES) {
    return 'File size must be less than 10MB';
  }
  return null;
}

export default function MobileReceipts() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setErrorMessage(validationError);
      showError(validationError);
      return;
    }

    setErrorMessage('');
    setUploadResult(null);
    setUploading(true);

    try {
      const result = await aiPurchasesAPI.upload(file);
      setUploadResult(result);
      success('Receipt uploaded and processed successfully!');
      setTimeout(() => {
        navigate('/tenant/purchases/ai-review');
      }, 2000);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Upload failed';
      setErrorMessage(errMsg);
      showError(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryClick = () => {
    if (uploading) return;
    galleryInputRef.current?.click();
  };

  const handleCameraClick = () => {
    if (uploading) return;
    cameraInputRef.current?.click();
  };

  return (
    <div className="px-4 py-6 pb-8">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Receipt Upload
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Upload a receipt or purchase invoice for AI-powered processing
      </p>

      {uploadResult ? (
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500 shrink-0" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Upload Successful
              </h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {uploadResult.message}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Redirecting to review...
            </p>
            <Button
              variant="primary"
              size="sm"
              className="min-h-[44px]"
              onClick={() => navigate('/tenant/purchases/ai-review')}
            >
              Go to Review
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* AI disclaimer */}
          <div className="mb-6 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                  AI Experimental
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Verify extracted data before saving.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {/* Gallery button */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full min-h-[52px] flex items-center justify-center gap-3 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
              onClick={handleGalleryClick}
              disabled={uploading}
            >
              <Upload className="w-5 h-5 shrink-0" aria-hidden />
              <span>Choose from gallery</span>
            </Button>

            {/* Take photo button */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full min-h-[52px] flex items-center justify-center gap-3 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
              onClick={handleCameraClick}
              disabled={uploading}
            >
              <Camera className="w-5 h-5 shrink-0" aria-hidden />
              <span>Take photo</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full min-h-[52px] flex items-center justify-center gap-3 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
              onClick={() => navigate('/tenant/mobile/receipts/manual-entry')}
              disabled={uploading}
            >
              <PencilLine className="w-5 h-5 shrink-0" aria-hidden />
              <span>Manual entry</span>
            </Button>
          </div>

          {uploading && (
            <div className="mt-6 flex flex-col items-center gap-3 py-6 rounded-lg bg-gray-100 dark:bg-gray-700/50">
              <Loader2 className="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" aria-hidden />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Uploading & processing...
              </p>
            </div>
          )}

          {errorMessage && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
            </div>
          )}

          <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
            PDF, JPG, PNG, GIF, WEBP — max 10MB
          </p>
        </>
      )}

      <ToastContainer />
    </div>
  );
}
